import { useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'
import clsx from 'clsx'

export interface ScrollingEllipsisTextProps {
  text: string
  /** Controlled trigger (e.g. a parent card's hover). Omit to trigger on own hover/focus. */
  active?: boolean
  /** Scroll pace — duration scales with the hidden length (rc-app: 25) */
  msPerPx?: number
  /** Time to glide back to the start once the trigger ends (rc-app: 1000) */
  returnMs?: number
  /** Pause before the scroll starts */
  delayMs?: number
  /** CSS timing function for both directions (rc-app: ease-out cubic) */
  easing?: string
  /** Soft-fade the clipped edges while scrolling (default) instead of rc-app's hard cut */
  fadeEdges?: boolean
  className?: string
}

export const EASE_OUT_CUBIC = 'cubic-bezier(0.215, 0.61, 0.355, 1)'

const FADE_PX = 8

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'
function subscribeReducedMotion(onChange: () => void) {
  const mq = window.matchMedia(REDUCED_MOTION)
  mq.addEventListener('change', onChange)
  return () => mq.removeEventListener('change', onChange)
}

/** Single-line text for small boxes (port of rc-app's ScrollableEllipsisText):
 *  ellipsis at rest; while triggered, the full string scrolls across to its end,
 *  then glides back and re-ellipsizes. Text that fits never moves.
 *
 *  Two stacked lines — a truncated one (rest) and a translated one (scrolling) —
 *  swapped via `visibility`; the swap back is delayed by `returnMs` so the glide
 *  home finishes first. All CSS transitions, so re-triggering mid-way is smooth. */
export function ScrollingEllipsisText({
  text,
  active,
  msPerPx = 25,
  returnMs = 1000,
  delayMs = 0,
  easing = EASE_OUT_CUBIC,
  fadeEdges = true,
  className,
}: ScrollingEllipsisTextProps) {
  const restRef = useRef<HTMLSpanElement>(null)
  const scrollRef = useRef<HTMLSpanElement>(null)
  const [overflowPx, setOverflowPx] = useState(0)
  const [selfTriggered, setSelfTriggered] = useState(false)
  const reducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    () => window.matchMedia(REDUCED_MOTION).matches
  )

  useLayoutEffect(() => {
    const box = restRef.current
    const full = scrollRef.current
    if (!box || !full) return
    // Fractional widths, rounded UP: scrollWidth/clientWidth are integers, so text
    // overflowing by a sub-pixel got an ellipsis from the browser but measured as
    // "fits" — leaving a "…" that hover never revealed.
    const measure = () =>
      setOverflowPx(
        Math.max(0, Math.ceil(full.getBoundingClientRect().width - box.getBoundingClientRect().width - 0.01))
      )
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(box)
    observer.observe(full)
    return () => observer.disconnect()
  }, [text])

  // Reduced motion: stay ellipsized — the title attribute still carries the full string
  const engaged = (active ?? selfTriggered) && overflowPx > 0 && !reducedMotion
  const selfHandlers =
    active === undefined
      ? {
          onMouseEnter: () => setSelfTriggered(true),
          onMouseLeave: () => setSelfTriggered(false),
          onFocus: () => setSelfTriggered(true),
          onBlur: () => setSelfTriggered(false),
        }
      : undefined

  // Travel a fade-width further so the end of the string comes to rest clear of the right fade
  const travelPx = overflowPx + (fadeEdges ? FADE_PX : 0)
  const swapBack = `visibility 0s linear ${engaged ? 0 : returnMs}ms`
  const edgeFade =
    engaged && fadeEdges
      ? `linear-gradient(to right, transparent 0, #000 ${FADE_PX}px, #000 calc(100% - ${FADE_PX}px), transparent 100%)`
      : undefined

  return (
    <span
      {...selfHandlers}
      title={overflowPx > 0 ? text : undefined}
      className={clsx('relative block min-w-0 overflow-hidden whitespace-nowrap', className)}
      style={{ maskImage: edgeFade, WebkitMaskImage: edgeFade }}
    >
      <span
        ref={restRef}
        className="block truncate"
        style={{ visibility: engaged ? 'hidden' : 'visible', transition: swapBack }}
      >
        {text}
      </span>
      <span
        ref={scrollRef}
        aria-hidden="true"
        className="absolute left-0 top-0 inline-block"
        style={{
          visibility: engaged ? 'visible' : 'hidden',
          transform: `translateX(${engaged ? -travelPx : 0}px)`,
          transition: [
            `transform ${engaged ? travelPx * msPerPx : returnMs}ms ${easing} ${engaged ? delayMs : 0}ms`,
            swapBack,
          ].join(', '),
        }}
      >
        {text}
      </span>
    </span>
  )
}
